import { Component, State, Prop, h } from '@stencil/core';
import send from './assets/images/send.svg';
import close from "./assets/images/close.svg";
import chatbotDefaultImage from './assets/images/chat-bot-robo-icon.svg';
import imageCloseicon from './assets/images/close-outline-icon.svg';
import imageUpload from './assets/images/image-upload-icon.svg';
import { marked, Renderer, Tokens } from "marked";
import DOMPurify from "dompurify";


const renderer = new Renderer();
renderer.link = ({ href, tokens }: Tokens.Link): string => {
  const text = tokens.map((token) => (token as Tokens.Text).raw).join(""); // Extract text safely
  const safeHref = DOMPurify.sanitize(href || "#");
  return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${text}</a>`;
};
marked.use({ renderer });
@Component({
  tag: 'chatbot-widget',
  styleUrl: 'chatbot-widget.css',
  shadow: true,
})
export class ChatbotWidget {
  @Prop() apiKey: string;
  @Prop() agentId: string;
  @Prop() emailId:  string;
  @Prop() userName: string ;

  @State() chatbotApiKey: string;
  @State() agentIdState: string;
  @State() messages: { text: string; type: 'user' | 'bot'; image?: string }[] = [];
  @State() loading: boolean = false;
  @State() closeStatus: boolean = true;

  @State() botName: string;
  @State() greetingsMessage: string;
  @State() textColor: string ;
  @State() botBubbleColor: string;
  @State() botImage: string;
  @State() chatBoxColor: string;
  @State() domainNames: string;
  @State() validOrigin: boolean = false;
  @State() closeBotMessageHint: boolean = true;
  @State() preprompts: any[] = [];
  @State() profileText: string;
  @State() entityName: string;
  @State() agentDeletionStatus: boolean; 
  @State() name:string;
  @State() email:string;
  @State() errors: { [key: string]: string } = {};
  @State() isChatBoxVisible: boolean = false;
  @State() isRegisterForm: boolean = false;
  @State() contactObjectId:String ;
  @State() callObjectId:String ;
  @State() contactEntityId:String;
  @State() phoneNumber:any = null;
  @State() model:String;
  @State() imageUploadErrorStatus: Boolean = false;
  @State() imageUploadErrorMsg: String;
  @State() imagePreview: string = '';
  @State() senderImageList: string = '';
  @State() uploadStatus: string = '';
  @State() isUploading: boolean = false;
  @State() showDate: string;
  @State() greetingLoaded: boolean = false;
  @State() footerDisclaimer: String;  
  @State() popupMessage: string;


  private chatInput!: HTMLTextAreaElement;
  private chatbox!: HTMLDivElement;
  private fileInput!: HTMLInputElement;


  private IMAGE_VALIDATION = {
    maxSize: 5 * 1024 * 1024, // 5MB in bytes
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png']
  };

  private validateImageFile(file: File): { isValid: boolean, error?: { title: string, description: string } } {
    if (!this.IMAGE_VALIDATION.allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: {
          title: "Invalid file type",
          description: "Please select a JPG, JPEG, or PNG image"
        }
      };
    }

    if (file.size > this.IMAGE_VALIDATION.maxSize) {
      return {
        isValid: false,
        error: {
          title: "File too large",
          description: "Please select an image smaller than 5MB"
        }
      };
    }
    return { isValid: true };
  }

  private handleFileChange = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    this.imageUploadErrorStatus = false
    if (!files || files.length === 0) {
      console.warn("No file selected.");
      return;
    }

    for (const file of Array.from(files)) {
      const validationResult = this.validateImageFile(file);
      if (!validationResult.isValid) {
        console.warn(validationResult.error?.title, validationResult.error?.description);
        this.imageUploadErrorMsg  = validationResult.error?.description;
        this.imageUploadErrorStatus = true;
        setTimeout(()=>{
          this.imageUploadErrorStatus = false
        }, 3000);
        continue;
      }

      try {
        const base64Image = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result);
          };
          reader.onerror = () => reject(new Error("Error reading file"));
          reader.readAsDataURL(file);
        });
        this.imagePreview = base64Image;
        this.senderImageList = base64Image;

      } catch (error) {
        console.error("File processing error:", error);
      }
    }
    input.value = "";
  };
  
  
  handleRemoveImage = () => {
    this.imagePreview = "";
    this.senderImageList = "";
  };


  validateForm() {
    let formIsValid = true;
    const errors: { [key: string]: string } = {};
  
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!this.name) {
      formIsValid = false;
      errors['name'] = 'Name is required.';
    } else if (!nameRegex.test(this.name)) {
      formIsValid = false;
      errors['name'] = 'Name must contain only letters and spaces.';
    }
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!this.email) {
      formIsValid = false;
      errors['email'] = 'Email is required.';
    } else if (!emailRegex.test(this.email)) {
      formIsValid = false;
      errors['email'] = 'Enter a valid email address.';
    }
  
    if (this.phoneNumber && !/^\d{10}$/.test(this.phoneNumber)) {
      formIsValid = false;
      errors['phone'] = 'Phone number must be 10 digits.';
    }
  
    this.errors = errors;
    return formIsValid;
  }

  private audioPlayer = async() => {
    try {
      const audio = new Audio('https://azure.healthvox.ai/assets/popup-sound.mp3');
      await audio.play();
    } catch (error) {
      // Silently handle any errors to prevent breaking the widget
      // console.log('Audio notification not available');
    }
  }

  private handleUserInteraction = () => {
    // If greeting has been loaded, play the audio
    if (this.greetingLoaded) {
      this.audioPlayer();
      this.greetingLoaded = false; // Reset so it only plays once
    }
    document.removeEventListener('click', this.handleUserInteraction);
    document.removeEventListener('scroll', this.handleUserInteraction);
  };

  componentWillLoad() {
    document.addEventListener('click', this.handleUserInteraction);
    document.addEventListener('scroll', this.handleUserInteraction);
    this.loadUserData()
    this.toggleRegisterForm();
    this.initializeChatbot();

    if(this.emailId && this.apiKey){
      this.email = this.emailId
      this.name = this.userName
      this.phoneNumber;
      this.sendingUserData();
    }
  }
  



  handleSubmit() {
    if (this.validateForm()) {
      this.sendingUserData();
      this.storeUserData();
      this.isChatBoxVisible = !this.isChatBoxVisible;
    }
  }

  handleSkipForm(){
    this.isChatBoxVisible = !this.isChatBoxVisible;
  }

  handleHintMessageBlock(){
    // Only hide the message temporarily for the current session
    // It will reappear when the chat is closed and reopened
    this.closeBotMessageHint = true;
  }

  handlePromptButtonClick = async(userMessage: string) => {
    if (this.loading) return;
    this.chatInput.value = '';
    this.messages = [...this.messages, { text: userMessage, type: 'user', image: this.imagePreview }];
    this.scrollToBottom();
    this.loading = true;
    if (!this.callObjectId) {
      await this.saveCall(); 
    }
    this.generateResponse(userMessage);
  }

  
  private async sendingUserData() {
    const payload = {
      name: this.name,
      email: this.email,
      phoneNumber: this.phoneNumber || undefined,
      apiKey: this.chatbotApiKey,
    };
  
    try {
      this.callObjectId = ''
      const response = await fetch("https://prod1.healthvox.ai/chatBot/savingContactFromWidget", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
      this.contactObjectId = data.objectId;
      this.contactEntityId =  data.entity.objectId;
    
      // console.log("User data saved:", data);
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  }

  private async saveCall(): Promise<void> {
    try {

      const response = await fetch("https://prod1.healthvox.ai/chatBot/save-call-details", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({agentId: this.agentIdState,
          contactId: this.contactObjectId,entityId:this.contactEntityId }),
      });
  
      const data = await response.json();
      this.callObjectId = data.objectId;

      if (response.ok) {
        // console.log("Call saved successfully:", data);
      } else {
        // console.error("Error saving call:", data);
      }
    } catch (error) {
      console.error("Error in saveCall function:", error);
    }
  }
  

  hexToRgb(hex: string): string {
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgb(${r}, ${g}, ${b})`;
  }

  hexToRgba(hex: string, opacity: number): string {
    const rgb = this.hexToRgb(hex);
    return rgb.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }
  
  toggleChatBox() {
    this.isChatBoxVisible = !this.isChatBoxVisible;
  }

  toggleRegisterForm() {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[a-zA-Z0-9-]+$/;

    if (
      (!emailPattern.test(this.emailId)) ||
      this.userName?.trim() === ""
    ) {
      this.isRegisterForm = true;  
      this.isChatBoxVisible = false; 
    } else {
      this.isRegisterForm = false;  
      this.isChatBoxVisible = true;  
    }
  }
  
  private storeUserData() {
    localStorage.setItem('userName', this.name);
    localStorage.setItem('userEmail', this.email);
    localStorage.setItem('userMobileNumber', JSON.stringify(this.phoneNumber))
  }

  private async initializeChatbot() {
    this.chatbotApiKey = this.apiKey;
    this.agentIdState = this.agentId;
    await this.validateApiKey();
    await this.fetchAgentSettings();
    setTimeout(() => {
      this.closeBotMessageHint = false;
    }, 3000);
    // if (!this.callObjectId) {
    //   await this.saveCall(); 
    // }
  }

  private async validateApiKey(): Promise<void> {
    try {
      const response = await fetch("https://prod1.healthvox.ai/chatBot/validateChatbotApiKey", {
        method: "POST",
        body: JSON.stringify({ apiKey: this.chatbotApiKey }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      await response.json();
      // console.log("API Key validated:", data);
    } catch (error) {
      console.error("Error in sending API key:", error);
    }
  }

  private async fetchAgentSettings(): Promise<void> {
    try {
      const response = await fetch("https://prod1.healthvox.ai/chatBot/getAgentSettingsData", {
        method: "POST",
        body: JSON.stringify({ apiKey: this.chatbotApiKey, agentId: this.agentIdState }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      // console.log(data)
      if (data) {
        this.botName = this.capitalizeName(data.name||"Agent");
        this.preprompts = this.filteringArray(data.prompts||[]);
        this.greetingsMessage = data.greeting|| "";
        this.botImage= data.chatBotImage || chatbotDefaultImage;
        this.textColor= data.textColor || "#ffffff";
        this.botBubbleColor = data.botBubbleColor || "#000000";
        this.chatBoxColor = data.botBoxColor || "#1b1464";
        this.domainNames = data.domains || [];
        this.entityName = this.capitalizeName(data.entity.name);
        this.agentDeletionStatus = data.isDeleted || false;
        this.model = data.model || "";
        this.footerDisclaimer = data.entity.footerDisclaimer || "";
        this.popupMessage = data.chatbotWelcomeMessage|| "Hi how can I help you?";
        
        // Mark greeting as loaded so audio will play on next user interaction
        this.greetingLoaded = true;
        
        // Ensure the greeting message is visible when the component loads
        if (this.closeStatus) {
          this.closeBotMessageHint = false;
        }
        
        // Try to play audio, but this might be blocked by browser
        try {
          setTimeout(() => {
            this.audioPlayer().catch(() => {
              // console.log('Audio will play on next user interaction');
            });
          }, 300);
        } catch (error) {
          // console.log('Audio will play on next user interaction');
        }
        
        const originUrl = window.location.origin;

        const abbreviation = this.entityName.split(" ").length > 1? this.entityName.split(" ").map(word => word[0]).join("").slice(0, 2): this.entityName.charAt(0);
        this.profileText = abbreviation

        // For local development, always set validOrigin to true
        const isLocalhost = originUrl.includes('localhost') || originUrl.includes('127.0.0.1');
        if (isLocalhost || (Array.isArray(this.domainNames) && this.domainNames.some(name => originUrl.includes(name)))) {
          this.validOrigin = true;
        } else {
          this.validOrigin = false;
        }
        this.showDate = this.getFormattedDate();
      }
    } catch (error) {
      console.error("Error fetching chatbot settings:", error);
    }
  }

  private capitalizeName(name: string): string {
    return name.replace(/\b\w/g, char => char.toUpperCase());
  }

  private filteringArray(arr){
    const filterList = arr.filter(i=> i!=="")
    return filterList
  }

  private async handleChat(): Promise<void> {
    if (this.loading) return;
    const userMessage = this.chatInput.value.trim();
    if (!userMessage) return;
    this.messages = [...this.messages, { text: userMessage, type: 'user', image: this.imagePreview }];
    this.chatInput.value = '';
    this.imagePreview = "";
    this.scrollToBottom();
    this.loading = true;
    if (!this.callObjectId) {
      await this.saveCall(); 
    }
    this.generateResponse(userMessage);
  }

  private async generateResponse(userQuery: string): Promise<void> {
    try {
      const response = await fetch("https://prod1.healthvox.ai/chatBot/getAiResponse", {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userQuery: userQuery,
          imageData: this.senderImageList? this.senderImageList.split(',')[1] : null,
          apiKey: this.chatbotApiKey,
          agentId: this.agentIdState,
          contactId: this.contactObjectId,
          entityId: this.contactEntityId,
          callId:this.callObjectId
        }),
      });
      const data = await response.json();
      const botResponseMessage = data && response.ok ? data.trim() : 'Oops! Something went wrong. Please try again.';
      this.messages = [...this.messages, { text: botResponseMessage, type: 'bot', image: null }];
    } catch (error) {
      console.error("Error generating response:", error);
      this.messages = [...this.messages, { text: 'Oops! Something went wrong. Please try again.', type: 'bot', image: null }];
    } finally {
      this.audioPlayer();
      this.loading = false;
      this.scrollToBottom();
      this.senderImageList = null ;
      this.chatInput.value="";
    }
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const chatbotWidget = document.querySelector('chatbot-widget');
      const shadowRoot = chatbotWidget?.shadowRoot;
      this.chatbox = shadowRoot?.querySelector('.ai-user-chatbox') as HTMLDivElement;
      if (this.chatbox) {
        this.chatbox.scrollTop = this.chatbox.scrollHeight;
      }
    });
  }

  private handleKeyPress(event: KeyboardEvent): void {
    // Prevent default behavior for spacebar to stop page scrolling
    if (event.key === ' ' || event.code === 'Space') {
      // Don't prevent default for spacebar as it should add space in the textarea
      // Just stop propagation to prevent page scrolling
      event.stopPropagation();
    }
    
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.loading) return;
      const message = this.chatInput?.value.trim();
      if (message) this.handleChat();
    }
  }


  private toggleChatWidget(): void {
    this.closeStatus = !this.closeStatus;
    this.scrollToBottom();
    
    // Show greeting message when chat is closed
    if (this.closeStatus) {
      this.closeBotMessageHint = false; // Show the greeting when chat is closed
    } else {
      this.closeBotMessageHint = true; // Hide the greeting when chat is open
    }
    
    // If greeting has been loaded but audio hasn't played yet, play it now
    if (this.greetingLoaded) {
      this.audioPlayer();
      this.greetingLoaded = false; // Reset so it only plays once
    }
  }

  private loadUserData() {
    const storedName = localStorage.getItem('userName');
    const storedEmail = localStorage.getItem('userEmail');
    const storedMobileNumber = localStorage.getItem('userMobileNumber');

    if (storedName) {
      this.userName = storedName;
      this.name = storedName;
    }

    if (storedEmail) {
      this.emailId = storedEmail;
      this.email = storedEmail;
    }
    if (storedMobileNumber){
      this.phoneNumber = JSON.parse(storedMobileNumber);
    }
  }

  

  private convertMarkdownToHTML(text: string): string | null {
    if (!text) return null;
    const rawHTML:any = marked.parse(text);
    return DOMPurify.sanitize(rawHTML, { ADD_ATTR: ["target"] });
  }


    getFormattedDate() {
      const date = new Date();
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }).format(date);
    }

  render() {
    if (!this.validOrigin || this.agentDeletionStatus) {
      return null;
    }
    return (
      <div class="ai-user-chat-container">
        {this.closeStatus? (
          <div class="ai-user-robot-container" >
            {this.closeBotMessageHint? null :
            <div class="chatbot-message-container">
                <div class="chatbot-message-blue" style={{ backgroundColor: this.chatBoxColor,  '--chat-box-color': this.chatBoxColor }} onClick={() => this.toggleChatWidget()}>
                    <p class="chatbot-message-content" style={{ color: this.textColor, }}>
                      <span class="wave-emoji">👋</span>
                      {this.popupMessage}
                    </p>
                    <div class="msg-close-btn-container" onClick={(e) => { e.stopPropagation(); this.handleHintMessageBlock(); }}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill={this.textColor} id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24" width="16" height="16"><path d="M12,0A12,12,0,1,0,24,12,12,12,0,0,0,12,0Zm4.707,15.293-1.414,1.414L12,13.414,8.707,16.707,7.293,15.293,10.586,12,7.293,8.707,8.707,7.293,12,10.586l3.293-3.293,1.414,1.414L13.414,12Z"/></svg>
                    </div>
                </div>
            </div>
            }
            <div class="chatbot-image-container" style={{ backgroundColor: this.chatBoxColor}}>
              <img 
                style={{ backgroundColor: this.chatBoxColor}}
                class="ai-chatbot-image" 
                src={this.botImage} 
                alt="chat-bot" 
                onClick={() => this.toggleChatWidget()} 
              />
            </div>
          </div>
        ) : (!this.isChatBoxVisible?
        (
          <div class="ai-user-chat-widget ai-user-chat-form">
            <div class="ai-chatbot-main-heading chatbot-form" style={{ backgroundColor: this.chatBoxColor }}>
              <div class="profile-heading-chatbot">
                  <h3 class="ai-form-heading " style={{ color: this.textColor }}>Chat With Us<span class="greeting-info">{`Welcome to ${this.entityName}!`}</span></h3>
              </div>
              <button class="ai-chatbot-btn ai-chatbot-btn-close" onClick={() => this.toggleChatWidget()}>
                <img class="ai-chatbot-close-image" src={close} alt="close" />
              </button>
            </div>
            <div class="ai-user-chatbox ai-user-chatbot-form-conatiner" id="chatBot" style={{border:"none"}}>
              <div class="chat-logs">
                <form>
                  <div class="form-group">
                    <label>
                      <span class="required">*</span>Name
                    </label>
                    <input
                      type="text"
                      value={this.name}
                      onInput={(e: any) => (this.name = e.target.value)}
                      class="form-control"
                    />
                    {this.errors['name'] && <div class="error-message">{this.errors['name']}</div>}
                  </div>
                  <div class="form-group">
                    <label>
                      <span class="required">*</span>Email
                    </label>
                    <input
                      type="email"
                      value={this.email}
                      onInput={(e: any) => (this.email = e.target.value)}
                      class="form-control"
                    />
                    {this.errors['email'] && <div class="error-message">{this.errors['email']}</div>}
                  </div>
                  <div class="form-group">
                    <label>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={this.phoneNumber}
                      pattern="[0-9]{10}"
                      maxlength="10"
                      onInput={(e: any) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        if (value.length <= 10) {
                          this.phoneNumber = value;
                        }
                      }}
                      class="form-control"
                    />
                    {this.errors['phone'] && <div class="error-message">{this.errors['phone']}</div>}
                  </div>
                </form>
                <div class="chatbot-form-buttons">
                  <button type="button"  class="btn-form-submit" onClick={() => this.handleSubmit()} style={{ backgroundColor: this.chatBoxColor, color: this.textColor, border: "none" }}>
                    Submit
                  </button>
              </div>
                </div>
            </div>
            <div class="chat-accreditation">
              Powered by <a class="accreditation" href="https://inleads.ai" target="_blank">Inleads.ai</a>
            </div>
          </div>
        ) : (
          <div class="ai-user-chat-widget ai-user-chatbot-widget">
            <div class="ai-chatbot-main-heading" style={{ backgroundColor: this.chatBoxColor }}>
              <div class="profile-heading-chatbot">
                {/* <h2 class="profile-abbreviation" style={{color: this.chatBoxColor}}>{this.profileText}</h2> */}
                <div 
                    class="profile-abbreviation-block" 
                    style={{
                      '--text-length': `${this.profileText.length}}`,
                    }}
                  >
                    <h3
                      class="profile-abbreviation" 
                      style={{
                        color: this.chatBoxColor,
                      }}
                    >
                      {this.profileText}
                    </h3>
                  </div>
                <h3 class="ai-user-heading" style={{ color: this.textColor }}>{this.entityName}<span class="agent-name">{this.botName}</span></h3>
              </div>
              <button class="ai-chatbot-btn ai-chatbot-btn-close" onClick={() => this.toggleChatWidget()}>
                <img class="ai-chatbot-close-image" src={close} alt="close" />
              </button>
            </div>
            <div class="ai-user-chatbox" id="chatBot">
                {this.showDate?
                <div class="show-date" style={{color: this.chatBoxColor}}>{this.showDate}</div>
                  : null
                }
                <div class='ai-chatbot-chat-message ai-chatbot-bot'>
                  <div innerHTML= {this.convertMarkdownToHTML(this.greetingsMessage)}></div>
                </div>
                {this.preprompts.length >0? (
                  <div class='ai-chatbot-chat-message ai-chatbot-user ai-chatbot-prompts'>
                    <div class="preprompts-container">
                          {this.preprompts.map((item, index) => ( item===""? null :
                            <button key={index} type='button' class="preprompt-button" style={{ '--chat-box-prompt-color': this.chatBoxColor}}
                            onClick={() => this.handlePromptButtonClick(item)} >{item}</button>
                          ))}
                      </div>
                  </div>
                ):null}
              {this.messages.map((message, index) => (
                <div class={`ai-chatbot-chat-message ai-chatbot-${message.type}`} 
                style={{
                  backgroundColor: message.type === 'user' ? this.hexToRgba(this.chatBoxColor, 0.25): '',  // Set RGBA color for user messages
                }} key={index}>
                  {message.type === 'user'?
                <div class="images-list-preview">
                {message.type==='user'&& message.image? (
                  <div class="image-preview-container sender-image-item" key={index}>
                    <img src={message.image} alt="Preview" class="image-preview image-sender" />
                  </div>
              ): null}
            </div>:null  
                }
                  <div innerHTML= {this.convertMarkdownToHTML(message.text)}></div>
                </div>
              ))}
              {this.loading && (
                <div class="ai-chatbot-loading">
                  <div class="ai-chatbot-music-loader">
                    <div class="ai-loading-bar" style={{ backgroundColor: this.botBubbleColor }}></div>
                    <div class="ai-loading-bar" style={{ backgroundColor: this.botBubbleColor }}></div>
                    <div class="ai-loading-bar" style={{ backgroundColor: this.botBubbleColor }}></div>
                    <div class="ai-loading-bar" style={{ backgroundColor: this.botBubbleColor }}></div>
                  </div>
                </div>
              )}
            </div>
            <div class="ai-chatbot-input-container">
              <div class="images-list-preview">
                {
                  this.imageUploadErrorStatus?<span class="image-upload-error">{`*${this.imageUploadErrorMsg}`}</span> : null
                }
              {this.imagePreview?
                <div class="image-preview-container image-input-preview">
                  <img src={this.imagePreview} alt="Preview" class="image-preview" />
                  <div class="close-image" onClick={() => this.handleRemoveImage()}>
                    <img src={imageCloseicon} alt="image-close-icon" class="close-icon" onClick={() => this.handleRemoveImage} />
                  </div>
                </div> : null
            }
              </div>
            <div class="user-input-container">
              {this.model === "llama3.2-vision:latest"?
              <div class="upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={this.handleFileChange}
                  ref={(el) => (this.fileInput = el as HTMLInputElement)}
                  style={{ display: "none" }}
                />
                <button
                  class="upload-button"
                  onClick={() => this.fileInput.click()}
                  disabled={this.isUploading}
                >
                  <img src={imageUpload} alt="image-upload"/>
                </button>
              </div>: null}
              <textarea
                class="ai-chatbot-textarea"
                placeholder="Type a message..."
                ref={(el) => (this.chatInput = el as HTMLTextAreaElement)}
                onKeyPress={(event) => this.handleKeyPress(event)}
                onKeyDown={(event) => {
                  // Prevent spacebar from scrolling the page
                  if (event.key === ' ' || event.code === 'Space') {
                    // Stop propagation to prevent the event from bubbling up to the document
                    // This prevents the page from scrolling
                    event.stopPropagation();
                  }
                }}
              ></textarea>
              <span
                class="ai-chatbot-send-button"
                onClick={() => this.handleChat()}
              >
                <img class="ai-chatbot-send-image" src={send} alt="send" />
              </span>
                </div>
            </div>
            {this.footerDisclaimer?
            <div class="ai-chatbot-disclaimer">
              {this.footerDisclaimer}
            </div> : <div class="ai-chatbot-disclaimer">Powered by Healthvox AI</div>}
          </div>
        ))}
      </div>
    );
  }
}
